/**
 * UserMenu — the signed-in person, top right: an avatar that opens name,
 * email, role and sign-out. The same menu MainViewHeader draws inline;
 * here as its own component so a page without the main header (/me) shows
 * the same thing. Guests count: a Lyyti-invited participant is signed in
 * and has a name, so they get the menu too.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { LogOut } from 'lucide-react';
import { withBackendPath } from '../config/api';
import { clearPinSession } from '../utils/pinSession';
import { useTranslation } from '../hooks/useTranslation';

const Wrap = styled.div`position: relative; flex: 0 0 auto;`;
const Avatar = styled.button`
  width: 36px; height: 36px; border-radius: 50%; border: 2px solid #4CAF50;
  background: rgba(59, 130, 246, 0.3); color: #fff; font-size: 14px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; padding: 0; overflow: hidden;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.5); transition: box-shadow 0.15s, transform 0.15s;
  &:hover { box-shadow: 0 0 14px rgba(76, 175, 80, 0.8); transform: scale(1.05); }
  &:focus-visible { outline: 2px solid #4cc7ce; outline-offset: 2px; }
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const Dropdown = styled.div`
  position: absolute; top: calc(100% + 8px); right: 0; min-width: 220px; z-index: 1000;
  background: rgba(20, 20, 30, 0.98); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4); backdrop-filter: blur(20px); overflow: hidden; padding: 8px;
`;
const Info = styled.div`padding: 10px 12px; border-bottom: 1px solid rgba(255,255,255,0.1); margin-bottom: 4px;`;
const Name = styled.div`font-size: 14px; font-weight: 600; color: #fff;`;
const Email = styled.div`font-size: 12px; color: rgba(255,255,255,0.5); margin-top: 2px; word-break: break-all;`;
const Role = styled.span`
  font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; padding: 2px 6px; border-radius: 4px;
  background: rgba(59,130,246,0.2); color: #93c5fd; margin-top: 6px; display: inline-block;
`;
const Item = styled.button`
  width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 12px; border: none; background: none;
  color: rgba(255,255,255,0.7); font-size: 13px; cursor: pointer; border-radius: 8px; text-align: left;
  &:hover { background: rgba(255,255,255,0.08); color: #fff; }
`;

export default function UserMenu({ user, onSignOut }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open]);
  if (!user) return null;
  const label = user.name || user.email || '?';
  const signOut = async () => {
    if (onSignOut) return onSignOut();
    await fetch(withBackendPath('/auth/logout'), { method: 'POST', credentials: 'include' }).catch(() => {});
    // The server ended this device's PIN session too (#1480); drop its marker with it.
    clearPinSession();
    // The landing page is the single sign-in entry point.
    window.location.href = '/';
    return undefined;
  };
  return (
    <Wrap ref={ref}>
      <Avatar type="button" onClick={() => setOpen((v) => !v)} title={label} aria-haspopup="menu" aria-expanded={open}>
        {user.photo_url ? <img src={user.photo_url} alt={label} /> : label[0].toUpperCase()}
      </Avatar>
      {open && (
        <Dropdown role="menu">
          <Info>
            {user.name && <Name>{user.name}</Name>}
            <Email>{user.email}</Email>
            {user.role && <Role>{user.role}</Role>}
          </Info>
          <Item type="button" role="menuitem" onClick={signOut}><LogOut size={14} /> {t('navigation.signOutShort')}</Item>
        </Dropdown>
      )}
    </Wrap>
  );
}
